import PropTypes from "prop-types";
import MDBox from "theme/components/MDBox";
import MDInput from "theme/components/MDInput";
import MDTypography from "theme/components/MDTypography";
import { useState, useEffect } from "react";
import { Snackbar, Alert, ListItem, ListItemText, Icon } from "@mui/material";
import MDButton from "theme/components/MDButton";

function PhoneNumberField({ formRef, draftIndex, displayName, fieldRef, options }) {
  const [fieldError, showFieldError] = useState(false);
  const [errMsg, setErrMsg] = useState("Invalid entry provided.");

  const [hasSavedValue, setHasSavedValue] = useState(false);
  const [draftValue, setDraftValue] = useState(null);

  useEffect(() => {
    if (!formRef) return;

    const saved = localStorage.getItem(`draft_${formRef}`);
    if (!saved) return;

    try {
      const parsed = JSON.parse(saved);
      const val = parsed?.[draftIndex].values?.[fieldRef];

      if (val !== undefined && val !== null && val !== "") {
        setDraftValue(val);
        setHasSavedValue(true);
      }
    } catch (e) {
      console.error("Draft parse error", e);
    }
  }, [formRef, fieldRef]);

  return (
    <MDBox>
      {hasSavedValue ? (
        <MDBox>
          <ListItem divider>
            <MDBox mr={1}>
              <Icon color="success">check_circle</Icon>
            </MDBox>
            <ListItemText
              primary={
                <MDTypography variant="h6" fontSize={14} fontWeight="regular">
                  {displayName}
                </MDTypography>
              }
              secondary={
                <MDTypography variant="body2" fontSize={13}>
                  {draftValue}
                </MDTypography>
              }
            />
            <MDButton
              size="small"
              color="warning"
              variant="outlined"
              iconOnly
              onClick={() => {
                try {
                  const key = `draft_${formRef}`;
                  const saved = localStorage.getItem(key);
                  if (!saved) return;

                  const parsed = JSON.parse(saved);

                  if (parsed?.values && fieldRef in parsed.values) {
                    delete parsed.values[fieldRef];
                  }

                  // persist updated draft
                  localStorage.setItem(key, JSON.stringify(parsed));

                  // update UI state
                  setHasSavedValue(false);
                  setDraftValue(null);
                } catch (e) {
                  console.error("Failed to clear draft field", e);
                }
              }}
            >
              <Icon>edit</Icon>
            </MDButton>
          </ListItem>
          <input
            type="hidden"
            id={`dff_${fieldRef}`}
            name={fieldRef}
            value={draftValue}
          />
        </MDBox>
      ) : (
        <MDBox>
          <MDBox ml={0.5}>
            <MDTypography variant="h6" fontSize={14} fontWeight="light">
              {displayName}
            </MDTypography>
          </MDBox>
          <MDInput
            name={fieldRef}
            id={`dff_${fieldRef}`}
            margin="dense"
            // label={displayName}
            type="text"
            fullWidth
            variant="outlined"
            onBlur={(e) => {
              const entryValue = e.target.value;
              console.log(options);

              if (typeof options.number_only !== "undefined") {
                const isnum = /^\d+$/.test(entryValue);
                if (!isnum) {
                  e.target.value = null;
                  setErrMsg(`${displayName} requires only digits`);
                  showFieldError(true);
                  return;
                }
              }

              if (typeof options.min_length !== "undefined") {
                if (entryValue.length < options.min_length) {
                  e.target.value = null;
                  setErrMsg(
                    `${displayName} must be atleast ${options.min_length} long`,
                  );
                  showFieldError(true);
                  return;
                }
              }

              if (typeof options.max_length !== "undefined") {
                if (entryValue.length > options.max_length) {
                  e.target.value = null;
                  setErrMsg(
                    `${displayName} cannot be longer ${options.max_length} characters`,
                  );
                  showFieldError(true);
                  return;
                }
              }

              if (typeof options.allowed_length !== "undefined") {
                if (entryValue.length !== options.allowed_length) {
                  e.target.value = null;
                  setErrMsg(
                    `${displayName} must be ${options.allowed_length} long`,
                  );
                  showFieldError(true);
                }
              }
            }}
            options={options}
          />
          <Snackbar
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            autoHideDuration={5000}
            onClose={() => showFieldError(false)}
            open={fieldError}
          >
            <Alert severity="error" sx={{ width: "100%" }}>
              {errMsg}
            </Alert>
          </Snackbar>
        </MDBox>
      )}
    </MDBox>
  );
}

// Typechecking props of the Transaction
PhoneNumberField.propTypes = {
  formRef: PropTypes.string.isRequired,
  fieldRef: PropTypes.string.isRequired,
  displayName: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.any),
};

PhoneNumberField.defaultProps = {
  options: null,
};

export default PhoneNumberField;
